import { useState, useEffect, useRef } from 'react';

import { useCampaignDataAttributes, useCampaignTypeContext, type CampaignTab } from 'features/campaigns';
import { scrollNearestScrollableToTop } from 'shared/utils/scroll';
import { testIds } from 'core/testIds';

import styles from './CampaignTabs.module.css';

interface CampaignTabsProps {
  tabsData?: CampaignTab[];
  selectedCampaignId?: string | null;
}

const CampaignTabs: React.FC<CampaignTabsProps> = ({ tabsData, selectedCampaignId }) => {
  const campaignType = useCampaignTypeContext();
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const tabCount = tabsData?.length || 0;

  const dataAttributes = useCampaignDataAttributes(selectedCampaignId ? { campaignId: selectedCampaignId } : {});
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickTab = (index: number) => {
    setActiveTabIndex(index);
  };

  // Reset tab to first tab (Games) when campaign changes
  useEffect(() => {
    setActiveTabIndex(0);
  }, [selectedCampaignId]);

  useEffect(() => {
    scrollNearestScrollableToTop(containerRef.current);
  }, [activeTabIndex]);

  const ActiveTabComponent = tabsData?.[activeTabIndex]?.component;

  return (
    <div ref={containerRef} className={styles.campaignTabsContainer} {...dataAttributes}>
      <div
        className={styles.tabsButtonContainer}
        data-tab-count={tabCount}
        style={
          {
            '--active-tab-index': activeTabIndex,
            '--tab-count': tabCount,
            '--tab-gaps': Math.max(tabCount - 1, 1),
          } as React.CSSProperties
        }
      >
        <div className={styles.tabIndicator} />
        {tabsData?.map((tab, index) => (
          <span
            key={index}
            className={`${styles.tabButton} ${activeTabIndex === index ? styles.activeTab : ''}`}
            onClick={() => handleClickTab(index)}
            data-testid={testIds.campaigns.tabButton(
              campaignType ?? '',
              tab.type,
              activeTabIndex === index ? 'active' : 'inactive',
            )}
          >
            {tab.label}
          </span>
        ))}
      </div>
      {ActiveTabComponent && <ActiveTabComponent />}
    </div>
  );
};

export default CampaignTabs;
